import { ReactNode } from 'react';
import { toast, Slide, ToastOptions } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  TriangleAlert,
  CircleAlert,
  OctagonAlert,
  X,
  RotateCw,
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import './toasts.css';

/** Utility to merge tailwind classes safely */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ------------------------------------------------------------------
// Base Configuration
// ------------------------------------------------------------------
const baseConfig: ToastOptions = {
  position: 'top-right',
  autoClose: 4000,
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  transition: Slide, // Keeps the smooth React-Toastify slide entrance
  closeButton: false,
  icon: false,
  style: {
    background: 'transparent',
    boxShadow: 'none',
    padding: 0,
  },
};

// ------------------------------------------------------------------
// Custom Toast Component
// ------------------------------------------------------------------
type ToastVariant = 'success' | 'warning' | 'info' | 'error';
type ToastTheme = 'tinted' | 'white';

interface CustomToastProps {
  title: string;
  message?: string | ReactNode;
  variant: ToastVariant;
  theme?: ToastTheme;
  closeToast?: () => void;
}

const variantStyles: Record<
  ToastVariant,
  { tinted: string; white: string; icon: any }
> = {
  success: {
    tinted: 'bg-green-100 text-green-800',
    white: 'bg-white text-gray-800',
    icon: CheckCircle2,
  },
  warning: {
    tinted: 'bg-amber-100 text-amber-800',
    white: 'bg-white text-gray-800',
    icon: TriangleAlert,
  },
  info: {
    tinted: 'bg-blue-100 text-blue-800',
    white: 'bg-white text-gray-800',
    icon: CircleAlert,
  },
  error: {
    tinted: 'bg-red-100 text-red-800',
    white: 'bg-white text-gray-800',
    icon: OctagonAlert,
  },
};

const iconColors: Record<ToastVariant, string> = {
  success: 'text-green-600',
  warning: 'text-amber-500',
  info: 'text-blue-600',
  error: 'text-red-600',
};

const CustomToast = ({
  title,
  message,
  variant,
  theme = 'tinted',
  closeToast,
}: CustomToastProps) => {
  const styles = variantStyles[variant];
  const Icon = styles.icon;

  return (
    <motion.div
      // Added Framer Motion for a subtle scale-in alongside the slide
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={cn(
        // FIX: Replaced hard w-[340px] with responsive w-full max-w-[340px] to prevent horizontal scroll on small devices
        'flex w-full max-w-[340px] items-center justify-between gap-3 rounded-full px-5 py-3.5 shadow-lg ring-1 ring-black/5',
        theme === 'tinted' ? styles.tinted : styles.white,
      )}
    >
      {/* Icon Area with pop animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
        className="flex-shrink-0 flex items-center justify-center"
      >
        {theme === 'tinted' ? (
          <Icon
            className={cn('h-5 w-5', iconColors[variant])}
            strokeWidth={2}
          />
        ) : (
          <Icon
            className={cn('h-5 w-5', iconColors[variant])}
            fill="currentColor"
            stroke="white"
            strokeWidth={2}
          />
        )}
      </motion.div>

      {/* Text Stack */}
      <div className="flex-1 flex flex-col justify-center">
        <span className="text-[14px] font-semibold tracking-tight leading-snug">
          {title}
        </span>
        {message && (
          <span
            className={cn(
              'text-[13px] leading-snug mt-0.5 font-medium',
              theme === 'tinted' ? 'opacity-75' : 'text-gray-500',
            )}
          >
            {message}
          </span>
        )}
      </div>

      {/* Close Button with Framer Motion tap scale */}
      <motion.button
        whileTap={{ scale: 0.85 }}
        whileHover={{ scale: 1.1 }}
        onClick={closeToast}
        className={cn(
          'flex-shrink-0 p-1.5 rounded-full transition-colors outline-none',
          theme === 'tinted'
            ? 'hover:bg-black/10 text-current opacity-70 hover:opacity-100'
            : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-200',
        )}
        aria-label="Close notification"
      >
        <X className="h-4 w-4" strokeWidth={2.5} />
      </motion.button>
    </motion.div>
  );
};

// ------------------------------------------------------------------
// Exported Notification Triggers (Unchanged functionality)
// ------------------------------------------------------------------

export interface NotifProps {
  title: string;
  message?: string | ReactNode;
  theme?: ToastTheme;
  config?: ToastOptions;
}

export const SuccessNotification = ({
  title,
  message,
  theme = 'tinted',
  config,
}: NotifProps) =>
  toast(
    <CustomToast
      title={title}
      message={message}
      variant="success"
      theme={theme}
    />,
    { ...baseConfig, ...config },
  );

export const ErrorNotification = ({
  title,
  message,
  theme = 'tinted',
  config,
}: NotifProps) =>
  toast(
    <CustomToast
      title={title}
      message={message}
      variant="error"
      theme={theme}
    />,
    { ...baseConfig, ...config },
  );

export const WarningNotification = ({
  title,
  message,
  theme = 'tinted',
  config,
}: NotifProps) =>
  toast(
    <CustomToast
      title={title}
      message={message}
      variant="warning"
      theme={theme}
    />,
    { ...baseConfig, ...config },
  );

export const InfoNotification = ({
  title,
  message,
  theme = 'tinted',
  config,
}: NotifProps) =>
  toast(
    <CustomToast
      title={title}
      message={message}
      variant="info"
      theme={theme}
    />,
    { ...baseConfig, ...config },
  );

export const PromiseToast = (
  promise: Promise<any>,
  messages: {
    pendingTitle?: string;
    successTitle: string;
    successMsg?: string;
    errorTitle: string;
    errorMsg?: string;
  },
  theme: ToastTheme = 'tinted',
) => {
  return toast.promise(
    promise,
    {
      pending: messages.pendingTitle
        ? {
            render: () => (
              <CustomToast
                title={messages.pendingTitle as string}
                variant="info"
                theme={theme}
              />
            ),
          }
        : undefined,
      success: {
        render: () => (
          <CustomToast
            title={messages.successTitle}
            message={messages.successMsg}
            variant="success"
            theme={theme}
          />
        ),
      },
      error: {
        render: () => (
          <CustomToast
            title={messages.errorTitle}
            message={messages.errorMsg}
            variant="error"
            theme={theme}
          />
        ),
      },
    },
    baseConfig,
  );
};

export const ReloadNotification = (
  message: string = 'Verification blocked. Please disable ad-blockers and tap to refresh.',
  reload?: () => Promise<void>,
) =>
  toast(
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileTap={{ scale: 0.98 }}
      className="flex w-full max-w-[340px] items-center justify-between gap-3 rounded-full px-5 py-3.5 shadow-lg ring-1 ring-black/5 bg-white text-gray-800 cursor-pointer group"
      role="button"
      onClick={() => (reload ? reload() : window.location.reload())}
    >
      <div className="flex-shrink-0 flex items-center justify-center">
        <RotateCw
          className="h-5 w-5 text-blue-500 transition-transform duration-500 group-hover:rotate-180"
          strokeWidth={2}
        />
      </div>

      <div className="flex-1 flex flex-col justify-center">
        <span className="text-[14px] font-semibold tracking-tight leading-snug">
          Action Required
        </span>
        <span className="text-[13px] leading-snug text-gray-500 mt-0.5 font-medium">
          {message}
        </span>
      </div>
    </motion.div>,
    {
      ...baseConfig,
      position: 'top-center',
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      toastId: 'reload-notification',
    },
  );
